import React from 'react';
import { Handle, Position } from 'reactflow';
import { FileText } from 'lucide-react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';

interface LogNodeData {
  label?: string;
  log?: {
    message?: string;
    level?: 'debug' | 'info' | 'warning' | 'error';
    outputVariables?: boolean;
  };
}

export default function LogNode({ 
  data, 
  selected 
}: { 
  data: LogNodeData; 
  selected?: boolean 
}) {
  const level = data.log?.level || 'info';
  
  // Color coding by log level
  const levelColors = {
    debug: 'text-muted-foreground',
    info: 'text-accent-blue',
    warning: 'text-status-warning',
    error: 'text-status-error',
  };
  
  const levelBadgeVariants = {
    debug: 'secondary' as const,
    info: 'default' as const,
    warning: 'outline' as const,
    error: 'destructive' as const,
  };

  const levelBadgeClasses = {
    debug: '',
    info: '',
    warning: 'border-status-warning text-status-warning',
    error: '',
  };

  return (
    <div className={cn(
      "min-w-[160px] rounded-lg",
      "bg-card",
      "border-2 transition-all duration-fast",
      selected
        ? `border-${level === 'error' ? 'status-error' : level === 'warning' ? 'status-warning' : 'accent-blue'} shadow-glow-focus`
        : `border-${level === 'error' ? 'status-error' : level === 'warning' ? 'status-warning' : 'accent-blue'}/50 dark:border-${level === 'error' ? 'status-error' : level === 'warning' ? 'status-warning' : 'accent-blue'}/30 shadow-md hover:shadow-lg`
    )}>
      {/* Connection Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className={cn(
          "!w-3 !h-3 !border-2",
          level === 'error' ? "!bg-status-error !border-status-error" :
          level === 'warning' ? "!bg-status-warning !border-status-warning" :
          "!bg-accent-blue !border-accent-blue"
        )}
        style={{ top: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border dark:border-border-subtle">
        <div className={cn(
          "w-6 h-6 rounded flex items-center justify-center",
          level === 'error' ? "bg-status-error-bg" :
          level === 'warning' ? "bg-status-warning-bg" :
          "bg-accent-blue/10 dark:bg-accent-blue/20"
        )}>
          <FileText className={cn("w-4 h-4", levelColors[level])} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            {data.label || 'Log Output'}
          </div>
          <div className="text-xs text-muted-foreground/70">
            {level.charAt(0).toUpperCase() + level.slice(1)}
          </div>
        </div>
        <Badge
          variant={levelBadgeVariants[level]}
          className={levelBadgeClasses[level]}
        >
          {level.toUpperCase()}
        </Badge>
      </div>

      {/* Node Body */}
      <div className="p-3 space-y-2">
        {data.log?.message && (
          <div className="text-xs">
            <span className="text-muted-foreground font-medium block mb-1">
              Message:
            </span>
            <span className="text-foreground font-mono text-xs block">
              {data.log.message}
            </span>
          </div>
        )}

        {data.log?.outputVariables && (
          <div className="flex items-center gap-1 pt-1">
            <span className="text-xs text-accent-blue font-medium">
              + Output Variables
            </span>
          </div>
        )}

        {!data.log?.message && (
          <div className="text-xs text-muted-foreground/70 italic">
            Configure message in properties
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className={cn(
          "!w-3 !h-3 !border-2",
          level === 'error' ? "!bg-status-error !border-status-error" :
          level === 'warning' ? "!bg-status-warning !border-status-warning" :
          "!bg-accent-blue !border-accent-blue"
        )}
        style={{ bottom: '-6px' }}
      />
    </div>
  );
}




